// SPDX-FileCopyrightText: Szilárd Hajba
// SPDX-License-Identifier: LGPL-3.0-or-later
import * as React from 'react';
import { dayjs, localeFirstDay, monthGridDays } from './utils/dates.js';
function Chevron({ dir }) {
    return (React.createElement("svg", { viewBox: "0 0 24 24", width: "1em", height: "1em", "aria-hidden": "true" },
        React.createElement("polyline", { points: dir === 'left' ? '15 18 9 12 15 6' : '9 18 15 12 9 6', fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" })));
}
/** Compact month navigator: prev/next month, click a day to pick it. */
export function MiniCalendar({ date, onDateChange, locale, firstDayOfWeek, prevLabel = 'Previous month', nextLabel = 'Next month', className }) {
    const firstDay = firstDayOfWeek ?? localeFirstDay(locale);
    const [viewMonth, setViewMonth] = React.useState(() => dayjs(date).startOf('month'));
    React.useEffect(function syncMonth() {
        setViewMonth(dayjs(date).startOf('month'));
    }, [date]);
    const anchor = viewMonth.format('YYYY-MM-DD');
    const cells = monthGridDays(anchor, firstDay);
    const selected = dayjs(date).format('YYYY-MM-DD');
    const today = dayjs().format('YYYY-MM-DD');
    const label = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(viewMonth.toDate());
    const dowFormat = new Intl.DateTimeFormat(locale, { weekday: 'narrow' });
    // Sun Jan 7 2024 is a known Sunday; advance by weekday index.
    const dowHeaders = Array.from({ length: 7 }, (_, i) => dowFormat.format(dayjs('2024-01-07')
        .add((firstDay + i) % 7, 'day')
        .toDate()));
    return (React.createElement("div", { className: className ? `c-cal-mini ${className}` : 'c-cal-mini' },
        React.createElement("div", { className: "c-cal-mini__nav" },
            React.createElement("button", { type: "button", className: "c-cal-mini__step", onClick: () => setViewMonth((m) => m.subtract(1, 'month')), "aria-label": prevLabel },
                React.createElement(Chevron, { dir: "left" })),
            React.createElement("strong", { "aria-live": "polite" }, label),
            React.createElement("button", { type: "button", className: "c-cal-mini__step", onClick: () => setViewMonth((m) => m.add(1, 'month')), "aria-label": nextLabel },
                React.createElement(Chevron, { dir: "right" }))),
        React.createElement("div", { className: "c-cal-mini__grid" },
            dowHeaders.map((dow, i) => (React.createElement("div", { key: `h${i}`, className: "c-cal-mini__header", "aria-hidden": "true" }, dow))),
            cells.map((iso) => {
                const d = dayjs(iso);
                const classes = ['c-cal-mini__cell'];
                if (d.month() !== viewMonth.month())
                    classes.push('other-month');
                if (iso === today)
                    classes.push('today');
                if (iso === selected)
                    classes.push('selected');
                return (React.createElement("button", { key: iso, type: "button", className: classes.join(' '), onClick: () => onDateChange(iso), "aria-current": iso === selected ? 'date' : undefined, "aria-label": new Intl.DateTimeFormat(locale, {
                        dateStyle: 'full'
                    }).format(d.toDate()) }, d.date()));
            }))));
}
// vim: ts=4
